import { CdkDrag, CdkDragDrop, CdkDragMove, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FlatTreeControl } from '@angular/cdk/tree';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ConfirmationDialogData } from '@app/shared/dialog/confirmation-dialog/confirmation-dialog.component';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { WithConfirmationDialog } from '@app/shared/dialog/with-confirmation-dialog.decorator';
import { StructureService } from '@app/shared/structure/structure.service';
import { MessageContextuelComponent, PegaseDialogType } from '@pcscol-portail/composants-pegase';
import { ConflitPiaDiplome, MaquettesService } from '@pcscol/odf-api-v1-angular';
import { Observable, Subject, Subscription, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, first, skip, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { CommandEvents } from '../../models/commands/command-events';
import { DropData, DropDataType } from '../../models/drop-data';
import { CommandFactoryService } from '../../services/command-factory.service';
import { TreeViewFiltres, TreeViewService } from '../../services/tree-view.service';
import { MaquetteNoeudComponent } from './maquette-noeud/maquette-noeud.component';
import { TreeNode, treeNodeWithFiltered } from './tree-node';
import {
    LigneEnfantReordonnable,
    PositionOrdonnancementAction,
    ReorderModeState,
    RepereInsertionOrdre,
    aLeMemeOrdre,
    calculerOrdreCibleDepuisAction,
    calculerProjectionOrdreEnfants,
    compterEnfantsDirects,
    estEnfantDirectDuParentReordonne,
    lireIdsEnfantsDirects
} from './treeview-ordonnancement';

enum ConflitPiaDialogType {
    RETIRER_PIA,
    SUPPORTING_OM,
    RETIRER_ENFANT
}

@Component({
    selector: 'app-treeview',
    templateUrl: './treeview.component.html',
    styleUrls: ['./treeview.component.scss'],
    imports: [
        MatCard,
        MatCardContent,
        MatTooltip,
        MessageContextuelComponent,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkDropList,
        CdkVirtualForOf,
        CdkDrag,
        MatIconButton,
        MatIcon,
        MaquetteNoeudComponent,
        NgTemplateOutlet,
        MatButton,
        MatMiniFabButton,
        RouterLink,
        AsyncPipe
    ]
})
export class TreeviewComponent implements OnInit, OnDestroy {
    private service = inject(TreeViewService);
    private maquettesService = inject(MaquettesService);
    private structureService = inject(StructureService);
    private notificationService = inject(NotificationService);
    private commandFactory = inject(CommandFactoryService);
    private events = inject(CommandEvents);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private treeView = inject(TreeViewService);
    private dialogService = inject(DialogService);

    @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport: CdkVirtualScrollViewport;
    private destroyed$ = new Subject<boolean>();
    private expandAllNodes: boolean = true;

    objetMaquetteSelected$ = this.treeView.objetMaquetteSelected$;
    private filtresPartiesArbre$: Observable<TreeViewFiltres> = this.treeView.filtresPartiesArbre$;

    private _transformer = (node: TreeNode, level: number) => {
        return {
            ...node,
            expandable: !!node.enfants && node.enfants.length > 0,
            level: level
        };
    };

    flatTreeControl = new FlatTreeControl<TreeNode, string>(
        node => node.level,
        node => node.enfants.length > 0,
        { trackBy: (dataNode: TreeNode) => dataNode.path }
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        node => node.enfants.map(e => e.objetMaquette) as any[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;
    dataSource = new MatTreeFlatDataSource(this.flatTreeControl, this.treeFlattener);
    reorderMode: ReorderModeState | null = null;
    repereInsertionOrdre: RepereInsertionOrdre | null = null;
    private enAttenteOrdonnancementParentPath: string | null = null;
    private idsEnfantsReordonnables: string[] = [];
    private lignesEnfantsReordonnables: LigneEnfantReordonnable[] = [];

    private subscriptions = new Subscription();

    public readonly racine$ = this.treeView.racineMaquette$;

    ngOnInit(): void {
        this.subscriptions.add(
            merge(
                this.service.refresh$.pipe(
                    tap(() => {
                        console.log('%crefresh$', 'color: orange');
                    })
                ),
                this.events.structureModifiee$.asObservable().pipe(
                    filter(x => !!x),
                    tap(() => {
                        console.log('%cstructureModifiee$', 'color: orange');
                    })
                ),
                this.events.objetMaquetteSupprime$.asObservable().pipe(
                    filter(x => !!x),
                    tap(() => {
                        console.log('%cobjetMaquetteSupprime$', 'color: orange');
                    })
                ),
                this.filtresPartiesArbre$.pipe(
                    skip(1),
                    takeUntil(this.destroyed$),
                    tap(() => {
                        this.quitterModeOrdreSiIndisponible();
                        console.log('%c filtres parties arbre');
                    })
                ),
                this.racine$.asObservable().pipe(
                    filter(x => !!x),
                    tap(x => {
                        console.log(
                            'racine reçue dans TreeviewComponent %c' + x?.code,
                            'color: blue; font-weight: bold; font-size: 1.1em;'
                        );
                        this.expandAllNodes = true;
                    }),
                    distinctUntilChanged((a, b) => a?.id === b?.id)
                )
            ).subscribe(() => {
                this.loadMaquette();
            })
        );
        this.subscriptions.add(
            this.service.expandNodeById$.subscribe((id: string) => {
                const node = this.findNodeById(id);
                if (node) {
                    this.flatTreeControl.expand(node);
                }
            })
        );
        this.subscriptions.add(
            this.service.expandTree$.subscribe((expand: boolean) => {
                if (expand) {
                    this.flatTreeControl.expandAll();
                } else {
                    this.flatTreeControl.collapseAll();
                }
            })
        );
        this.initRechercheTexteListener();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.destroyed$.next(true);
        this.destroyed$.complete();
        this.treeView.racineMaquetteId = null;
    }

    drop(event: CdkDragDrop<DropData>) {
        if (this.reorderMode) {
            this.reordonnerEnfant(event);
            return;
        }
        // ignore drops outside of the tree
        if (!event.isPointerOverContainer) {
            return;
        }
        const objetMaquette = this.findTargetNode(event);
        if (objetMaquette == null) {
            // le drop a eu lieu en dehors de l'arbre (dans le treeview, mais hors treenode)
            return;
        }

        const enfantId = event.item.data.objetMaquetteId;
        if (enfantId == null) {
            const message = `Erreur interne lors du drag & drop: l'event ne porte pas l'identifiant de l'objetMaquette dragged: ${event}`;
            console.error(message, event);
            throw new Error(message);
        }
        this.ajouterEnfant(objetMaquette, enfantId);
    }

    private selectObjetMaquette(node: TreeNode | null) {
        this.treeView.selectObjetMaquette(node);
    }

    dragDataObjetMaquette(node: TreeNode): DropData {
        return {
            type: DropDataType.OBJET_MAQUETTE,
            objetMaquetteId: node.id,
            objetMaquettePath: node.path
        };
    }

    ajouterEnfant(parent: TreeNode, objetMaquetteEnfant: string) {
        this.commandFactory
            .ajouterEnfantCommand(parent.id, '0', { id: objetMaquetteEnfant, obligatoire: true })
            .do()
            .pipe(first())
            .subscribe(/*_ => this.loadMaquette()*/);
    }

    retirerEnfant(treeNode: TreeNode, ignoreConflict: boolean = false) {
        const parent = this.parent(treeNode);
        this.commandFactory
            .retirerEnfantCommand(parent!.id, '0', { id: treeNode.id, obligatoire: treeNode.obligatoire }, ignoreConflict)
            .do()
            .pipe(first())
            .subscribe(
                () => {},
                error => this.handleErrorConflitPiaDiplome(error, treeNode, ConflitPiaDialogType.RETIRER_ENFANT)
            );
    }

    ajouterPIA(treeNode: TreeNode) {
        this.commandFactory
            .declarerPointInscriptionAdministrativeCommand(treeNode.id, '0', treeNode.contexteId!)
            .do()
            .pipe(first())
            .subscribe(() => {
                this.loadMaquette();
                this.notificationService.success(`Le point d'inscription administrative a été ajouté avec succès.`);
            });
    }

    retirerPIA(treeNode: TreeNode, ignoreConflict: boolean = false) {
        this.commandFactory
            .retirerPointInscriptionAdministrativeCommand(treeNode.id, '0', treeNode.contexteId!, ignoreConflict)
            .do()
            .pipe(first())
            .subscribe(
                () => {
                    this.loadMaquette();
                    this.notificationService.success(`Le point d'inscription administrative a été retiré avec succès.`);
                },
                error => this.handleErrorConflitPiaDiplome(error, treeNode, ConflitPiaDialogType.RETIRER_PIA)
            );
    }

    @WithConfirmationDialog({
        titre: 'Veuillez confirmer la modification.',
        messageTexte: [
            args =>
                args[0].valide
                    ? `Vous allez modifier le caractère des liens entre des objets maquettes déjà validés.`
                    : `Vous êtes sur le point de rendre ${args[1] ? 'obligatoire' : 'facultatif'} l'objet maquette ${args[0].code}, ${
                          args[0].libelle
                      }.`,
            args => (args[0].valide ? `Si vous confirmez, ces modifications seront propagées dans les autres modules de Pégase.` : ``)
        ],
        type: args => (args[0].valide ? PegaseDialogType.WARN : PegaseDialogType.INFO)
    })
    modifierLien(treeNode: TreeNode, obligatoire: boolean) {
        const parent = this.parent(treeNode);
        this.commandFactory
            .modifierLienEnfantCommand(parent!.id, '0', treeNode.id, obligatoire)
            .do()
            .pipe(first())
            .subscribe(() => this.loadMaquette());
    }

    basculerModeOrdre(parent: TreeNode) {
        if (this.reorderMode?.parentPath === parent.path) {
            this.quitterModeOrdre();
            return;
        }
        this.expandAllAtNodeLevel(parent);
        if (!this.peutUtiliserReordonnancement()) {
            return;
        }

        if (compterEnfantsDirects(parent) < 2) {
            return;
        }

        this.restaurerModeOrdre();
        this.selectObjetMaquette(parent);
        this.reorderMode = {
            parentId: parent.id,
            parentPath: parent.path,
            parentCode: parent.code,
            expandedNodePaths: this.calculerCheminsNoeudsDeveloppes(parent),
            enAttente: false
        };
        this.appliquerModeOrdre();
    }

    quitterModeOrdre() {
        this.restaurerModeOrdre();
    }

    afficherRepereInsertionAvant(node: TreeNode): boolean {
        return (
            this.isNodeReordonnable(node) &&
            this.repereInsertionOrdre?.idNoeud === node.id &&
            this.repereInsertionOrdre.position === 'before'
        );
    }

    afficherRepereInsertionApres(node: TreeNode): boolean {
        return (
            this.isNodeReordonnable(node) &&
            this.repereInsertionOrdre?.idNoeud === node.id &&
            this.repereInsertionOrdre.position === 'after'
        );
    }

    isNodeReordonnable(node: TreeNode): boolean {
        return estEnfantDirectDuParentReordonne(node, this.reorderMode);
    }

    isParentReordonne(node: TreeNode): boolean {
        return this.reorderMode?.parentPath === node.path;
    }

    canUseEnfantReorderActions(node: TreeNode): boolean {
        if (node.racine || !this.peutUtiliserReordonnancement()) {
            return false;
        }

        return compterEnfantsDirects(this.parent(node)) > 1;
    }

    canMoveEnfantToDebut(node: TreeNode): boolean {
        if (!this.peutUtiliserReordonnancement()) {
            return false;
        }

        const idsEnfants = lireIdsEnfantsDirects(this.parent(node));
        return idsEnfants.length > 1 && idsEnfants[0] !== node.id;
    }

    canMoveEnfantToPrevious(node: TreeNode): boolean {
        return this.canMoveEnfantToDebut(node);
    }

    canMoveEnfantToNext(node: TreeNode): boolean {
        return this.canMoveEnfantToFin(node);
    }

    canMoveEnfantToFin(node: TreeNode): boolean {
        if (!this.peutUtiliserReordonnancement()) {
            return false;
        }

        const idsEnfants = lireIdsEnfantsDirects(this.parent(node));
        return idsEnfants.length > 1 && idsEnfants[idsEnfants.length - 1] !== node.id;
    }

    canModifierOrdreEnfants(node: TreeNode): boolean {
        return this.peutUtiliserReordonnancement() && (this.isParentReordonne(node) || compterEnfantsDirects(node) > 1);
    }

    isTreeToggleDisabled(node: TreeNode): boolean {
        if (!this.reorderMode) {
            return false;
        }

        return this.isParentReordonne(node) || estEnfantDirectDuParentReordonne(node, this.reorderMode);
    }

    preparerLignesEnfantsReordonnables() {
        if (!this.reorderMode?.parentPath) {
            this.viderCacheLignesEnfantsReordonnables();
            return;
        }

        const parent = this.findNodeByPath(this.reorderMode.parentPath);
        if (!parent) {
            this.viderCacheLignesEnfantsReordonnables();
            return;
        }

        this.idsEnfantsReordonnables = lireIdsEnfantsDirects(parent);
        this.lignesEnfantsReordonnables = this.lireLignesEnfantsReordonnables(
            this.idsEnfantsReordonnables,
            this.virtualScrollViewport.elementRef.nativeElement as HTMLElement
        );
    }

    mettreAJourRepereInsertion(event: CdkDragMove<DropData>) {
        if (!this.reorderMode?.parentPath || this.reorderMode.enAttente) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const idEnfantDeplace = event.source.data?.objetMaquetteId;
        if (!idEnfantDeplace) {
            this.reinitialiserRepereInsertion();
            return;
        }

        if (!this.idsEnfantsReordonnables.length || !this.lignesEnfantsReordonnables.length) {
            this.preparerLignesEnfantsReordonnables();
        }

        if (!this.idsEnfantsReordonnables.length || !this.lignesEnfantsReordonnables.length) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const projection = calculerProjectionOrdreEnfants(
            this.idsEnfantsReordonnables,
            idEnfantDeplace,
            event.pointerPosition.y,
            this.lignesEnfantsReordonnables
        );
        this.repereInsertionOrdre = projection?.repereInsertion ?? null;
    }

    reinitialiserRepereInsertion() {
        this.repereInsertionOrdre = null;
        this.viderCacheLignesEnfantsReordonnables();
    }

    placerEnfantAuDebut(treeNode: TreeNode) {
        this.reordonnerEnfantDepuisAction(treeNode, 'debut');
    }

    monterEnfant(treeNode: TreeNode) {
        this.reordonnerEnfantDepuisAction(treeNode, 'precedent');
    }

    descendreEnfant(treeNode: TreeNode) {
        this.reordonnerEnfantDepuisAction(treeNode, 'suivant');
    }

    placerEnfantALaFin(treeNode: TreeNode) {
        this.reordonnerEnfantDepuisAction(treeNode, 'fin');
    }

    private reordonnerEnfant(event: CdkDragDrop<DropData>) {
        if (!this.reorderMode?.parentPath || this.reorderMode.enAttente || !event.isPointerOverContainer) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const idEnfantDeplace = event.item.data?.objetMaquetteId;
        const pathEnfantDeplace = event.item.data?.objetMaquettePath;
        if (!idEnfantDeplace) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const parent = this.findNodeByPath(this.reorderMode.parentPath);
        if (!parent) {
            this.reorderMode = null;
            this.reinitialiserRepereInsertion();
            return;
        }

        const enfantDeplace = pathEnfantDeplace ? this.findNodeByPath(pathEnfantDeplace) : null;
        if (enfantDeplace && !estEnfantDirectDuParentReordonne(enfantDeplace, this.reorderMode)) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const idsEnfantsActuels = parent.enfants.map(enfant => enfant.objetMaquette.id);
        if (!idsEnfantsActuels.includes(idEnfantDeplace)) {
            this.reinitialiserRepereInsertion();
            return;
        }

        const projection = calculerProjectionOrdreEnfants(
            idsEnfantsActuels,
            idEnfantDeplace,
            event.dropPoint.y,
            this.lireLignesEnfantsReordonnables(idsEnfantsActuels, event.container.element.nativeElement as HTMLElement)
        );
        this.reinitialiserRepereInsertion();
        const nouvelOrdreEnfants = projection?.nouvelOrdreEnfants;
        if (!nouvelOrdreEnfants || aLeMemeOrdre(idsEnfantsActuels, nouvelOrdreEnfants)) {
            return;
        }

        this.executerOrdonnancement(parent, idEnfantDeplace, idsEnfantsActuels, nouvelOrdreEnfants);
    }

    supprimer(treeNode: TreeNode, ignoreConflict: boolean = false) {
        this.commandFactory
            .supprimerObjetMaquetteCommand(treeNode.id, '0', ignoreConflict)
            .do()
            .subscribe(
                () => {
                    this.selectObjetMaquette(null);
                    this.notificationService.success(`L'objet maquette ${treeNode.code} ${treeNode.libelle} a été supprimé avec succès.`);
                    if (treeNode.id === this.racine$.getValue()?.id) {
                        this.router.navigate(['../'], { relativeTo: this.route });
                    } else {
                        this.treeView.refresh();
                    }
                },
                error => this.handleErrorConflitPiaDiplome(error, treeNode, ConflitPiaDialogType.SUPPORTING_OM)
            );
    }

    private loadMaquette(): void {
        console.log('LOAD OBJET MAQUETTE, racine : %c' + this.racine$.getValue()?.id, 'color: green; font-weight: bold; font-size: 1.1em;');
        console.groupEnd();

        if (this.racine$.getValue()?.id) {
            this.subscriptions.add(
                this.maquettesService
                    .lireStructureMaquette(this.structureService.current$.value, this.racine$.getValue()!.id!)
                    .pipe(
                        takeUntil(this.destroyed$),
                        withLatestFrom(this.filtresPartiesArbre$),
                        tap(([maquette, filtres]) => {
                            console.log('maquette', maquette);

                            const filteredNodes = treeNodeWithFiltered(maquette.racine as TreeNode, filtres);
                            console.log('filteredNodes', filteredNodes);

                            this.dataSource.data = filteredNodes.nodes;
                            if (filteredNodes.branchesVidesPartieBasse) {
                                this.notificationService.info(
                                    `Certaines branches de la maquette n'ont pas de point d'inscription et n'apparaissent pas après filtrage.`
                                );
                            }
                        }),
                        switchMap(([a]) => of(a)) // TODO
                    )
                    .subscribe(
                        () => {
                            if (this.expandAllNodes) {
                                this.flatTreeControl.expandAll();
                            }
                            this.expandAllNodes = false;
                            this.quitterModeOrdreSiIndisponible();
                            this.appliquerModeOrdre();
                            this.finaliserOrdonnancementEnAttente();
                        },
                        error => this.gererErreurChargementMaquette(error)
                    )
            );
        }
    }

    private findTargetNode(event: CdkDragDrop<DropData>): TreeNode | null {
        // attention, on recherche l'info directement dans le DOM, donc peut mieux faire
        // une idée serait soit de mieux maîtriser le drag & drop et les tree (le drag & drop est surtout fait entre des listes)
        // ou sinon d'étudier un peu plus l'exemple https://stackblitz.com/edit/mat-tree-with-drag-and-drop
        // mais il me paraît pas mal overkill
        // voir aussi
        const dropElement = event.container.element.nativeElement.ownerDocument.elementFromPoint(event.dropPoint.x, event.dropPoint.y);
        if (dropElement == null) {
            throw new Error(`Erreur interne lors du drag & drop: aucune élément DOM aux coordonnées ${event.dropPoint}`);
        }

        const noeudElement = dropElement.closest('app-maquette-noeud');
        if (noeudElement == null) {
            // peut survenir dans le cas où on drop en dehors des nodes, mais dans le div du tree-node
            console.log(`drag & drop: drop détecté en dehors de l'arbre ${noeudElement}`, noeudElement);
            return null;
        }
        const pathObjetMaquette = noeudElement.getAttribute('data-path');
        if (pathObjetMaquette == null) {
            throw new Error(`Erreur interne lors du drag & drop: aucun noeud n'a pu être récupéré à partir du DOM ${dropElement}`);
        }

        const objetMaquette = this.findNodeByPath(pathObjetMaquette);
        if (objetMaquette == null) {
            throw new Error(`Erreur interne lors du drag & drop: l'objet maquette ${pathObjetMaquette} est introuvable`);
        }
        return objetMaquette;
    }

    private findNodeById(id: string, from?: TreeNode): TreeNode | null {
        const start: TreeNode = from ?? (this.dataSource.data?.[0] as TreeNode);
        if (!start) {
            return null;
        }
        if (start.id === id) {
            return start;
        }
        for (const enfant of start.enfants) {
            const found = this.findNodeById(id, enfant.objetMaquette);
            if (found != null) {
                return found;
            }
        }
        return null;
    }

    private findNodeByPath(path: string): TreeNode | null {
        return this.flatTreeControl.dataNodes?.find(node => node.path === path) ?? null;
    }

    private parent(treeNode: TreeNode): TreeNode | null {
        const indexSeparateurParent = treeNode.path.lastIndexOf('.');
        if (indexSeparateurParent < 0) {
            return null;
        }

        return this.findNodeByPath(treeNode.path.substring(0, indexSeparateurParent));
    }

    /**
     * Retourne tous le noeud en paramètre ainsi que tous ses descendants
     */
    private calculerNoeuds(node: TreeNode): TreeNode[] {
        return [node, ...node.enfants.flatMap(e => this.calculerNoeuds(e.objetMaquette))];
    }

    private calculerCheminsNoeudsDeveloppes(parent: TreeNode): string[] {
        return this.calculerNoeuds(parent)
            .filter(node => node.path !== parent.path && this.flatTreeControl.isExpanded(node))
            .map(node => node.path);
    }

    private appliquerModeOrdre() {
        if (!this.reorderMode) {
            return;
        }

        const parent = this.findNodeByPath(this.reorderMode.parentPath);
        if (!parent) {
            this.reorderMode = null;
            return;
        }

        this.reorderMode = {
            ...this.reorderMode,
            parentPath: parent.path,
            parentCode: parent.code
        };
        this.flatTreeControl.expand(parent);
        this.flatTreeControl.collapseDescendants(parent);
        this.flatTreeControl.expand(parent);
    }

    private restaurerModeOrdre() {
        const reorderMode = this.reorderMode;
        this.reorderMode = null;
        this.reinitialiserRepereInsertion();
        if (!reorderMode) {
            return;
        }

        reorderMode.expandedNodePaths.forEach(path => {
            const node = this.findNodeByPath(path);
            if (node) {
                this.flatTreeControl.expand(node);
            }
        });
    }

    private reordonnerEnfantDepuisAction(treeNode: TreeNode, position: PositionOrdonnancementAction) {
        const parent = this.parent(treeNode);
        if (!parent) {
            return;
        }

        const idsEnfantsActuels = lireIdsEnfantsDirects(parent);
        const idsEnfantsCibles = calculerOrdreCibleDepuisAction(idsEnfantsActuels, treeNode.id, position);
        if (!idsEnfantsCibles) {
            return;
        }

        if (aLeMemeOrdre(idsEnfantsActuels, idsEnfantsCibles)) {
            return;
        }

        this.executerOrdonnancement(parent, treeNode.id, idsEnfantsActuels, idsEnfantsCibles);
    }

    private executerOrdonnancement(parent: TreeNode, idEnfant: string, idsEnfantsActuels: string[], idsEnfantsCibles: string[]) {
        if (this.reorderMode?.parentPath === parent.path) {
            this.reorderMode = { ...this.reorderMode, enAttente: true };
        }

        this.commandFactory
            .ordonnerEnfantCommand(parent.id, idEnfant, idsEnfantsActuels, idsEnfantsCibles)
            .do()
            .pipe(first())
            .subscribe(
                () => {
                    if (this.reorderMode?.parentPath === parent.path) {
                        // Le refresh structureModifiee$ remettra le mode ordre en état disponible.
                        this.enAttenteOrdonnancementParentPath = parent.path;
                    }
                    this.reinitialiserRepereInsertion();
                    if (this.reorderMode?.parentPath !== parent.path) {
                        this.notificationService.success(`L'ordre des enfants a été modifié avec succès.`);
                    }
                },
                error => {
                    this.annulerOrdonnancementEnAttente(error, parent.path);
                }
            );
    }

    private peutUtiliserReordonnancement(): boolean {
        const filtres = this.treeView.filtresPartiesArbreActuels;
        return filtres.partieHaute && filtres.partieBasse && !this.treeView.rechercheTexteActuelle;
    }

    private quitterModeOrdreSiIndisponible() {
        if (!this.reorderMode || this.peutUtiliserReordonnancement()) {
            return;
        }

        this.restaurerModeOrdre();
    }

    private finaliserOrdonnancementEnAttente() {
        if (!this.enAttenteOrdonnancementParentPath) {
            return;
        }

        const reorderMode = this.reorderMode;
        if (reorderMode && reorderMode.parentPath === this.enAttenteOrdonnancementParentPath) {
            this.reorderMode = { ...reorderMode, enAttente: false };
        }

        this.enAttenteOrdonnancementParentPath = null;
        this.notificationService.success(`L'ordre des enfants a été modifié avec succès.`);
    }

    private annulerOrdonnancementEnAttente(error, parentPath?: string) {
        const reorderMode = this.reorderMode;
        if (reorderMode && (reorderMode.parentPath === parentPath || reorderMode.enAttente)) {
            this.reorderMode = { ...reorderMode, enAttente: false };
        }

        this.enAttenteOrdonnancementParentPath = null;
        this.reinitialiserRepereInsertion();
        this.notificationService.error(error);
    }

    private gererErreurChargementMaquette(error) {
        this.annulerOrdonnancementEnAttente(error);
    }

    private lireLignesEnfantsReordonnables(idsEnfantsActuels: string[], conteneur: HTMLElement): LigneEnfantReordonnable[] {
        const elementsReordonnables = Array.from(conteneur.querySelectorAll<HTMLElement>('.node[data-reorderable="true"]'));
        const idsEnfantsAutorises = new Set(idsEnfantsActuels);
        const lignes: LigneEnfantReordonnable[] = [];

        for (const element of elementsReordonnables) {
            const idEnfant = element.dataset.id;
            if (!idEnfant || !idsEnfantsAutorises.has(idEnfant)) {
                continue;
            }

            const rectangle = element.getBoundingClientRect();
            lignes.push({
                idEnfant,
                hautY: rectangle.top,
                milieuY: rectangle.top + rectangle.height / 2,
                basY: rectangle.bottom
            });
        }

        return lignes.sort((a, b) => a.milieuY - b.milieuY);
    }

    private viderCacheLignesEnfantsReordonnables() {
        this.idsEnfantsReordonnables = [];
        this.lignesEnfantsReordonnables = [];
    }

    initRechercheTexteListener() {
        const base = this.treeView.rechercheTexteDansArbre$;
        base.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe((recherche: string | null) => {
            this.quitterModeOrdreSiIndisponible();
            this.flatTreeControl.dataNodes?.forEach(n => (n.containsText = false));
            if (recherche) {
                const nodesFound = this.flatTreeControl.dataNodes.filter(
                    n => n.libelle?.toLowerCase().includes(recherche) || n.code?.toLowerCase().includes(recherche)
                );
                this.treeView._rechercheActuelleCount.next({ index: 0, totalElements: nodesFound.length });
                if (nodesFound.length) {
                    nodesFound.forEach(n => (n.containsText = true));
                    const index = this.flatTreeControl.dataNodes.findIndex(n => n.path === nodesFound[0].path);
                    this.virtualScrollViewport.scrollToIndex(index, 'smooth');
                }
            } else {
                this.treeView._rechercheActuelleCount.next(null);
            }
        });

        this.treeView.rechercheActuelleCount$.pipe(withLatestFrom(base), takeUntil(this.destroyed$)).subscribe(([v, recherche]) => {
            if (recherche && v?.totalElements) {
                const nodesFound = this.flatTreeControl.dataNodes.filter(
                    n => n.libelle?.toLowerCase().includes(recherche!) || n.code?.toLowerCase().includes(recherche!)
                );
                if (nodesFound.length && v?.index) {
                    const index = this.flatTreeControl.dataNodes.findIndex(n => n.path === nodesFound[v.index!].path);
                    this.virtualScrollViewport.scrollToIndex(index, 'smooth');
                    this.treeView.selectObjetMaquette(this.flatTreeControl.dataNodes[index]);
                }
            }
        });
    }

    expandAllAtNodeLevel(node: TreeNode) {
        this.flatTreeControl.collapseAll();
        this.flatTreeControl.dataNodes
            .filter(n => n.level < node.level)
            .forEach(n => {
                this.flatTreeControl.expand(n);
            });
    }

    hasPIAInChildren(om: TreeNode): boolean {
        return this.recursiveSearchForPIAInChildren(om);
    }

    private recursiveSearchForPIAInChildren(om: TreeNode): boolean {
        return om.enfants?.some(c => c.objetMaquette.pia || this.recursiveSearchForPIAInChildren(c.objetMaquette));
    }

    private handleErrorConflitPiaDiplome(error: HttpErrorResponse, treeNode: TreeNode, dialogType: ConflitPiaDialogType) {
        if (error.status === 409) {
            this.onOpenDissocierNiveauxDiplomantsModal((error.error as ConflitPiaDiplome).codesDiplomes, treeNode, dialogType);
        } else {
            this.notificationService.error(error);
        }
    }

    onOpenDissocierNiveauxDiplomantsModal(
        listeCodeDiplomesConflitPia: Array<string>,
        treeNode: TreeNode,
        dialogType: ConflitPiaDialogType
    ) {
        const pluriel: boolean = listeCodeDiplomesConflitPia.length > 0;
        const data: ConfirmationDialogData = {
            titre: `Avertissement : PIA lié au${pluriel && 'x'} diplôme${pluriel && 's'}`,
            messageTexte: [
                `Vous allez détacher, supprimer ou retirer le caractère PIA d'un objet lié au${pluriel && 'x'} diplôme${pluriel && 's'} : `,
                ...listeCodeDiplomesConflitPia,
                `Souhaitez-vous continuer ?`
            ]
        };
        this.dialogService.openConfirmationDialog(data).subscribe((confirmation: boolean) => {
            if (confirmation) {
                switch (dialogType) {
                    case ConflitPiaDialogType.RETIRER_PIA:
                        this.retirerPIA(treeNode, true);
                        break;
                    case ConflitPiaDialogType.RETIRER_ENFANT:
                        this.retirerEnfant(treeNode, true);
                        break;
                    case ConflitPiaDialogType.SUPPORTING_OM:
                        this.supprimer(treeNode, true);
                        break;
                }
            }
        });
    }
}
